import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faExpand, faVolumeUp, faVolumeMute, faCompress } from '@fortawesome/free-solid-svg-icons';
import VideoThumbnail from './VideoThumbnail';

// Animações
const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.2); }
  50% { box-shadow: 0 0 20px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.2); }
`;

const dataFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
`;

const borderFlash = keyframes`
  0% { border-color: rgba(64, 128, 255, 0.3); }
  25% { border-color: rgba(100, 200, 255, 0.5); }
  50% { border-color: rgba(64, 128, 255, 0.7); }
  75% { border-color: rgba(100, 200, 255, 0.5); }
  100% { border-color: rgba(64, 128, 255, 0.3); }
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(0.95); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.7; }
`;

// Componentes estilizados
const VideoContainer = styled.div`
  position: relative;
  width: 70%;
  max-width: 700px;
  margin: 1rem auto 2rem;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid ${props => props.color || 'rgba(64, 128, 255, 0.3)'};
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    width: 85%;
  }
`;

const VideoElement = styled.video`
  width: 100%;
  aspect-ratio: 16/9;
  z-index: 0;
  background: #000;
  will-change: transform, opacity; /* Otimização para GPU */
  transform: translateZ(0); /* Força aceleração de hardware */
  backface-visibility: hidden; /* Reduzir problemas de renderização */
  perspective: 1000; /* Ajuda na aceleração 3D */
  cursor: pointer; /* Adicionar cursor pointer para indicar que é clicável */
  
  &::-webkit-media-controls {
    display: none !important;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 3;
`;

const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.color || 'rgba(64, 128, 255, 0.7)'};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 32px;
  box-shadow: 0 0 30px ${props => props.color || 'rgba(64, 128, 255, 0.7)'};
  cursor: pointer;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 40px ${props => props.color || 'rgba(64, 128, 255, 0.9)'};
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 16px;
  }
`;

const VideoTitle = styled.div`
  color: white;
  margin-top: 10px;
  font-size: 14px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.7);
`;

const PulsingTitle = styled(VideoTitle)`
  animation: ${pulseAnimation} 2s infinite ease-in-out;
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 5;
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  margin: 0 5px;
  padding: 5px;
  
  &:hover {
    color: ${props => props.color || '#4080ff'};
  }
  
  &:focus {
    outline: none;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 5px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 10px;
  position: relative;
  cursor: pointer;
`;

const Progress = styled.div`
  height: 100%;
  background: ${props => props.color || '#4080ff'};
  width: ${props => props.width || '0%'};
`;

const LogoOverlay = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 4;
  display: none;
  
  img {
    height: 40px;
    
    @media (max-width: 768px) {
      height: 30px;
    }
  }
`;

const VideoClickArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  cursor: pointer;
`;

const FullscreenMessageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
`;

const FullscreenMessage = styled.div`
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  text-align: center;
  padding: 10px;
  border-radius: 8px;
  width: 85%;
  max-width: 220px;
  border: 1px solid ${props => props.color || '#4080ff'};
  font-size: 12px;
`;

const FocusButton = styled.button`
  background-color: ${props => props.color || '#4080ff'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  margin-top: 8px;
  cursor: pointer;
  font-size: 11px;
`;

const LoadingRing = styled.div`
  display: inline-block;
  width: 50px;
  height: 50px;
  margin-bottom: 10px;
  
  &:after {
    content: " ";
    display: block;
    width: 40px;
    height: 40px;
    margin: 5px;
    border-radius: 50%;
    border: 5px solid ${props => props.color || '#fff'};
    border-color: ${props => props.color || '#fff'} transparent ${props => props.color || '#fff'} transparent;
    animation: ${spinAnimation} 1.2s linear infinite;
  }
`;

const LoadingProgress = styled.div`
  width: 150px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  margin: 10px auto;
  overflow: hidden;
  position: relative;
`;

const LoadingProgressBar = styled.div`
  height: 100%;
  background: ${props => props.color || '#4080ff'};
  width: ${props => props.width || '0%'};
  border-radius: 4px;
  transition: width 0.3s ease;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: ${dataFlow} 1.5s infinite linear;
  }
`;

// Função JavaScript para abrir o vídeo em uma nova janela com um player nativo quando no modo fullscreen
// Esta função é independente do React para evitar problemas de renderização
function openNativePlayerWindow(videoSrc, currentTime, isMuted) {
  // Essa função vai ser injetada na nova janela para controlar o vídeo
  const playerScript = `
    document.addEventListener('DOMContentLoaded', function() {
      const video = document.getElementById('fullscreen-video');
      if (video) {
        video.currentTime = ${currentTime || 0};
        video.muted = ${isMuted};
        video.controls = true;
        
        // Tentar reproduzir automaticamente
        video.play().catch(function(e) { 
          console.warn('Não foi possível reproduzir automaticamente:', e); 
        });
        
        // Lidar com o fechamento da janela
        window.addEventListener('unload', function() {
          try {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage({
                type: 'fullscreenClosed',
                currentTime: video.currentTime,
                paused: video.paused
              }, '*');
            }
          } catch(e) {
            console.warn('Erro ao comunicar com janela principal:', e);
          }
        });
      }
    });
  `;

  // HTML simples para a nova janela com o player nativo
  const playerHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Player de Vídeo</title>
      <style>
        body, html { 
          margin: 0; 
          padding: 0; 
          width: 100%; 
          height: 100%; 
          overflow: hidden;
          background-color: #000;
        }
        #fullscreen-video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      </style>
      <script>${playerScript}</script>
    </head>
    <body>
      <video id="fullscreen-video" src="${videoSrc}" autoplay controlsList="nodownload" disablePictureInPicture></video>
    </body>
    </html>
  `;

  // Abrir uma nova janela com o tamanho máximo possível
  const windowFeatures = "popup,fullscreen=yes,width=" + window.screen.width + ",height=" + window.screen.height;
  const playerWindow = window.open("", "videoPlayer", windowFeatures);
  
  if (playerWindow) {
    playerWindow.document.write(playerHTML);
    playerWindow.document.close();
    
    // Focar na nova janela
    playerWindow.focus();
    
    return playerWindow;
  }
  
  return null;
}

const PlatformVideo = ({ platformId, platformColor }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerWindow, setPlayerWindow] = useState(null);
  const [savedVideoTime, setSavedVideoTime] = useState(0); // Tempo salvo do vídeo quando o fullscreen é fechado
  
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const videoContainerRef = useRef(null);
  const progressBarRef = useRef(null);
  
  // Mapeamento de plataformas para vídeos locais
  const videoUrls = {
    twilio: '/videos/twilio.mp4',
    vapi: '/videos/VAPI.mp4',
    openai: '/videos/OPENAI.mp4'
  };
  
  // Configurações de qualidade de vídeo
  const videoQualities = {
    twilio: { height: 720, bitrate: '1.5M' },
    vapi: { height: 720, bitrate: '1.5M' },
    openai: { height: 720, bitrate: '1.5M' }
  };
  
  // Mapeamento de plataformas para títulos
  const videoTitles = {
    twilio: 'Guia de Configuração do Twilio',
    vapi: 'Guia de Configuração do Vapi',
    openai: 'Guia de Configuração do OpenAI'
  };

  // Mapeamento de plataformas para nomes
  const platformNames = {
    twilio: 'Twilio',
    vapi: 'Vapi',
    openai: 'OpenAI'
  };

  // Mapeamento de plataformas para logos
  const platformLogos = {
    twilio: '/twilio_logo.png',
    vapi: '/vapi_logo.png',
    openai: '/openai_logo.png'
  };

  // Mensagem de volta da janela de fullscreen
  useEffect(() => {
    const handleMessage = (event) => {
      // Verificar se a mensagem veio da janela do player
      if (playerWindow && event.source === playerWindow) {
        const data = event.data;
        
        if (data && data.type === 'fullscreenClosed') {
          // Salvar o tempo atual do vídeo antes de resetar
          if (typeof data.currentTime === 'number') {
            setSavedVideoTime(data.currentTime);
          }
          
          // Resetar para o estado de miniatura
          setShowVideo(false);
          setIsPlaying(false);
          setPlayerWindow(null);
          
          // Pausar o vídeo
          if (videoRef.current) {
            videoRef.current.pause();
          }
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [playerWindow]);
  
  // Verifica se a janela de fullscreen foi fechada
  useEffect(() => {
    if (playerWindow) {
      const checkWindow = setInterval(() => {
        if (playerWindow && playerWindow.closed) {
          // Não podemos obter o tempo do vídeo fechado sem mensagem, mas podemos usar o último tempo conhecido
          // Se não tivermos recebido mensagem, usar o último tempo salvo internamente
          
          // Resetar para o estado de miniatura
          setShowVideo(false);
          setIsPlaying(false);
          setPlayerWindow(null);
          
          // Pausar o vídeo
          if (videoRef.current) {
            videoRef.current.pause();
          }
          
          clearInterval(checkWindow);
        }
      }, 500);
      
      return () => {
        clearInterval(checkWindow);
      };
    }
  }, [playerWindow]);

  const handlePlay = () => {
    setIsLoading(true);
    setShowVideo(true);
    setIsPlaying(true);
    setIsMuted(false);
    setShowControls(true);
    
    // Se temos um tempo salvo do vídeo, vamos usá-lo para iniciar no ponto correto
    if (savedVideoTime > 0) {
      // Definir um timeout para dar tempo do vídeo carregar antes de definir o tempo
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = savedVideoTime;
        }
      }, 100);
    }
    
    // Esconder os controles após 3 segundos
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };
  
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };
  
  const handleMouseEnter = () => {
    setShowControls(true);
  };
  
  const handleMouseLeave = () => {
    // Esconder os controles após 2 segundos se o vídeo estiver em reprodução
    if (isPlaying) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  };
  
  const handleTogglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        playVideo(videoRef.current);
      }
    }
  };

  // Função auxiliar para reproduzir o vídeo com tratamento de erro
  const playVideo = (videoElement) => {
    if (!videoElement) return;
    
    videoElement.play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch(error => {
        console.error("Erro ao reproduzir vídeo:", error);
        setVideoError(true);
      });
  };
  
  const handleToggleMute = () => {
    setIsMuted(!isMuted);
    
    // Aplicar o estado mudo no elemento de vídeo
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };
  
  // Nova implementação radical de fullscreen usando uma janela separada
  const handleFullScreen = () => {
    // Obter a URL absoluta do vídeo
    const videoUrl = new URL(videoUrls[platformId], window.location.origin).href;
    
    // Obter o tempo atual do vídeo
    const time = videoRef.current ? videoRef.current.currentTime : 0;
    
    // Pausar o vídeo original
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    
    // Abrir a nova janela com o player nativo
    const newWindow = openNativePlayerWindow(videoUrl, time, isMuted);
    
    if (newWindow) {
      setPlayerWindow(newWindow);
      setIsPlaying(false); // Pausar o vídeo na janela principal
    } else {
      // Se não foi possível abrir a janela, mostrar um alerta
      console.warn('Não foi possível abrir o modo de tela cheia. Verifique se os pop-ups estão permitidos.');
    }
  };

  // Função para focar na janela de fullscreen
  const focusFullscreenWindow = () => {
    if (playerWindow && !playerWindow.closed) {
      playerWindow.focus();
    }
  };
  
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      try {
        setCurrentTime(videoRef.current.currentTime);
        
        if (!isNaN(videoRef.current.duration)) {
          setDuration(videoRef.current.duration);
        }
      } catch (err) {
        console.warn("Erro ao atualizar o tempo:", err);
      }
    }
  };
  
  const handleProgressClick = (e) => {
    if (progressBarRef.current && videoRef.current) {
      try {
        const rect = progressBarRef.current.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        videoRef.current.currentTime = pos * videoRef.current.duration;
      } catch (err) {
        console.warn("Erro ao clicar na barra de progresso:", err);
      }
    }
  };
  
  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  // Limpeza quando o componente é desmontado
  useEffect(() => {
    return () => {
      // Limpar todos os timeouts
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      // Limpar o vídeo
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      }
      
      // Fechar a janela do player se estiver aberta
      if (playerWindow && !playerWindow.closed) {
        playerWindow.close();
      }
    };
  }, [playerWindow]);
  
  // Tratar o carregamento do vídeo
  const handleCanPlay = () => {
    setIsLoading(false);
    if (isPlaying && videoRef.current) {
      playVideo(videoRef.current);
    }
  };
  
  // Tratar o progresso de carregamento
  const handleProgress = () => {
    if (videoRef.current) {
      try {
        const duration = videoRef.current.duration;
        const buffered = videoRef.current.buffered;
        
        if (duration > 0 && buffered && buffered.length > 0) {
          const bufferedEnd = buffered.end(buffered.length - 1);
          const progress = Math.round((bufferedEnd / duration) * 100);
          setLoadProgress(progress);
        }
      } catch (err) {
        // Ignorar silenciosamente
      }
    }
  };
  
  // Função para tentar novamente após um erro
  const handleRetry = () => {
    setVideoError(false);
    setIsLoading(true);
    
    if (videoRef.current) {
      videoRef.current.load();
      
      // Pequeno atraso antes de tentar reproduzir novamente
      setTimeout(() => {
        if (videoRef.current) {
          playVideo(videoRef.current);
        }
      }, 500);
    }
  };
  
  return (
    <>
      {!showVideo ? (
        <div onClick={handlePlay} style={{ cursor: 'pointer' }}>
          <VideoThumbnail 
            platformId={platformId} 
            platformColor={platformColor}
            platformName={platformNames[platformId]}
            logoSrc={platformLogos[platformId]}
          />
        </div>
      ) : (
        <VideoContainer 
          ref={videoContainerRef}
          color={platformColor} 
          onMouseEnter={handleMouseEnter} 
          onMouseLeave={handleMouseLeave}
        >
          <VideoElement
            ref={videoRef}
            src={videoUrls[platformId]}
            preload="auto"
            playsInline
            muted={isMuted}
            autoPlay
            onCanPlay={handleCanPlay}
            onEnded={() => setIsPlaying(false)}
            onPause={handlePause}
            onProgress={handleProgress}
            onTimeUpdate={handleTimeUpdate}
            onDurationChange={(e) => {
              if (e.target.duration && !isNaN(e.target.duration)) {
                setDuration(e.target.duration);
              }
            }}
            onWaiting={() => setIsLoading(true)}
            onPlaying={() => setIsLoading(false)}
            onError={(e) => {
              console.error("Erro no vídeo:", e);
              setIsLoading(false);
              setIsPlaying(false);
              setVideoError(true);
            }}
          />
          
          {/* Área clicável para pausar/reproduzir o vídeo */}
          {!isLoading && !videoError && !playerWindow && (
            <VideoClickArea onClick={handleTogglePlay} />
          )}
          
          {/* Aviso de vídeo em reprodução em outra janela */}
          {playerWindow && (
            <FullscreenMessageOverlay>
              <FullscreenMessage color={platformColor}>
                <div>Reprodução em modo de tela cheia</div>
                <div style={{ fontSize: '10px', marginTop: '6px', opacity: 0.8 }}>
                  (Se não conseguir localizar a janela, clique no botão abaixo)
                </div>
                <FocusButton color={platformColor} onClick={focusFullscreenWindow}>
                  Focar na janela de tela cheia
                </FocusButton>
              </FullscreenMessage>
            </FullscreenMessageOverlay>
          )}
          
          <LogoOverlay>
            <img src={platformLogos[platformId]} alt={platformNames[platformId]} />
          </LogoOverlay>
          
          {isLoading && !videoError && !playerWindow && (
            <VideoOverlay>
              <div style={{ textAlign: 'center' }}>
                <LoadingRing color={platformColor} />
                <LoadingProgress>
                  <LoadingProgressBar color={platformColor} width={`${loadProgress || 0}%`} />
                </LoadingProgress>
                <PulsingTitle>
                  {loadProgress > 0 ? `Carregando ${loadProgress}%` : 'Inicializando vídeo...'}
                </PulsingTitle>
              </div>
            </VideoOverlay>
          )}
          
          {videoError && !playerWindow && (
            <VideoOverlay>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: 'white', marginBottom: '15px' }}>
                  Ocorreu um erro ao carregar o vídeo.
                </div>
                <PlayButton color={platformColor} onClick={handleRetry}>
                  <FontAwesomeIcon icon={faPlay} />
                </PlayButton>
                <VideoTitle>Tentar novamente</VideoTitle>
              </div>
            </VideoOverlay>
          )}
          
          <VideoControls show={showControls || !isPlaying}>
            <ControlButton color={platformColor} onClick={handleTogglePlay} disabled={!!playerWindow}>
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </ControlButton>
            
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, marginLeft: '10px', marginRight: '10px' }}>
              <span style={{ color: 'white', fontSize: '12px', marginRight: '10px', whiteSpace: 'nowrap' }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              
              <ProgressBar ref={progressBarRef} onClick={playerWindow ? null : handleProgressClick} style={{ flex: 1 }}>
                <Progress color={platformColor} width={`${(currentTime / duration) * 100 || 0}%`} />
              </ProgressBar>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ControlButton color={platformColor} onClick={handleToggleMute}>
                <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
              </ControlButton>
              
              <ControlButton color={platformColor} onClick={playerWindow ? focusFullscreenWindow : handleFullScreen}>
                <FontAwesomeIcon icon={playerWindow ? faExpand : faExpand} />
              </ControlButton>
            </div>
          </VideoControls>
        </VideoContainer>
      )}
    </>
  );
};

export default PlatformVideo;
